import { Link } from 'react-router-dom';
import { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-start gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
          <img src={article.imageUrl} alt={article.title} className="w-full h-full object-cover object-top" loading="lazy" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h3>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
            <span>{article.date}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
      >
        <div className="relative h-52 overflow-hidden bg-background-200">
          <img src={article.imageUrl} alt={article.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
          {article.hot && (
            <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium whitespace-nowrap">
              热门
            </span>
          )}
          <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-background-950/60 text-background-50 text-xs whitespace-nowrap backdrop-blur-sm">
            {article.categoryName}
          </span>
        </div>
        <div className="p-5">
          <h3 className="font-heading text-lg text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 line-clamp-2 leading-relaxed">
            {article.summary}
          </p>
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-background-200/70">
            <div className="flex items-center gap-3 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
              </span>
              <span>{article.date}</span>
            </div>
            <span className="text-xs text-foreground-400">{article.source}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
    >
      <div className="relative h-44 overflow-hidden bg-background-200">
        <img src={article.imageUrl} alt={article.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        {article.hot && (
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium whitespace-nowrap">
            热门
          </span>
        )}
      </div>
      <div className="p-4">
        <span className="inline-block px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-xs mb-2 whitespace-nowrap">
          {article.categoryName}
        </span>
        <h3 className="font-heading text-base text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 line-clamp-2 leading-relaxed">
          {article.summary}
        </p>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/70">
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
            <span>{article.date}</span>
          </div>
          <span className="text-xs text-foreground-400">{article.source}</span>
        </div>
      </div>
    </Link>
  );
}